import type { User } from '@supabase/supabase-js';
import { supabase } from '../cloud/supabaseClient';

export type AppRole = 'owner' | 'manager' | 'cashier';

export type Profile = {
  id: string;
  full_name: string;
  role: AppRole;
  branch_id: string | null;
  active: boolean;
};

export function isManagementProfile(profile: Profile | null): profile is Profile {
  return profile !== null && ['owner', 'manager'].includes(profile.role);
}

export async function signIn(email: string, password: string): Promise<User> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  if (!data.user) throw new Error('Authentication succeeded without a user.');
  const { error: auditError } = await supabase.rpc('record_auth_audit', { p_action: 'LOGIN' });
  if (auditError) {
    await supabase.auth.signOut();
    throw new Error('Authentication audit could not be recorded. Please try again.');
  }
  return data.user;
}

export async function signOut(): Promise<void> {
  try {
    await supabase.rpc('record_auth_audit', { p_action: 'LOGOUT' });
  } catch {
    /* Best-effort audit before session termination. */
  }
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function getCurrentProfile(): Promise<Profile | null> {
  const user = (await getCurrentSession())?.user;
  if (!user) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id,full_name,role,branch_id,active')
    .eq('id', user.id)
    .single();
  if (error) throw error;
  return data as Profile;
}

export function subscribeToAuthChanges(callback: (user: User | null) => void) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) =>
    callback(session?.user ?? null),
  );
  return () => data.subscription.unsubscribe();
}
